import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Target, Users, Award } from 'lucide-react';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const About = () => {
  useEffect(() => {
    document.title = "Green Energy Consulting - Om os";
  }, []);

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/images/about-us.png" 
            alt="" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/80 to-slate-950"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.span 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-emerald-400 font-bold text-sm tracking-[0.3em] uppercase mb-4 block"
          >
            Hvem er vi
          </motion.span>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-bold text-white mb-8"
          >
            Om Green Energy Consulting
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed"
          >
            Vi er specialister i at navigere i komplekse tekniske projekter. Med rødder i solid engineering og fokus på fremtidens grønne løsninger.
          </motion.p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              {...fadeIn}
              className="relative"
            >
              <div className="aspect-[4/3] overflow-hidden border border-slate-100 shadow-2xl relative z-10">
                <img 
                  src="/images/about-us.png" 
                  alt="Vores arbejde i marken" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-emerald-500 -z-0 hidden md:block"></div>
              <div className="absolute -top-6 -left-6 w-32 h-32 border-2 border-slate-100 -z-0 hidden md:block"></div>
            </motion.div>

            <motion.div 
              {...fadeIn}
              transition={{ delay: 0.2 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Vores tilgang og værdier</h2>
                <p className="text-slate-600 text-lg leading-relaxed mb-6">
                  Hos Green Energy Consulting ApS arbejder vi i krydsfeltet mellem projektledelse, el‑engineering og grøn omstilling. Vi skaber struktur, sikkerhed og fremdrift – med respekt for standarder, kvalitet og samarbejde.
                </p>
                <p className="text-slate-600 text-lg leading-relaxed">
                  Vores fundament er bygget på årtiers erfaring fra tung industri, Pharma og PtX. Vi forstår vigtigheden af både de store linjer og de tekniske detaljer, der gør forskellen mellem et godt projekt og et fantastisk resultat.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { title: "Specialiseret viden", desc: "Dyb indsigt i DS/EN standarder og komplekse el-anlæg." },
                  { title: "Resultatorienteret", desc: "Vi fokuserer på levering til tiden og inden for budget." },
                  { title: "Sikkerhed først", desc: "HSEQ og arbejdsmiljø er altid øverst på dagsordenen." },
                  { title: "Grøn omstilling", desc: "Vi hjælper virksomheder med at realisere deres bæredygtige mål." }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">{item.title}</h4>
                      <p className="text-sm text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
            <motion.div {...fadeIn} className="bg-white p-10 shadow-sm border border-slate-100">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <Target className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Mission</h3>
              <p className="text-slate-600">At levere teknisk rådgivning i verdensklasse, der driver den grønne omstilling fremad gennem sikkerhed og ekspertise.</p>
            </motion.div>

            <motion.div {...fadeIn} transition={{ delay: 0.1 }} className="bg-white p-10 shadow-sm border border-slate-100">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Samarbejde</h3>
              <p className="text-slate-600">Vi tror på, at de bedste løsninger skabes gennem tæt dialog og gennemsigtighed med vores kunder og partnere.</p>
            </motion.div>

            <motion.div {...fadeIn} transition={{ delay: 0.2 }} className="bg-white p-10 shadow-sm border border-slate-100">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Kvalitet</h3>
              <p className="text-slate-600">Vi går aldrig på kompromis med kvaliteten af vores arbejde eller de standarder, vi opererer under.</p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
